import React, { useEffect, useMemo, useState } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import {
    Alert,
    Button,
    Collapse,
    Grid,
    Group,
    Select,
    Stack,
    Text,
    TextInput,
    UnstyledButton,
} from '@mantine/core';
import { IconChevronDown, IconExclamationCircle } from '@tabler/icons-react';
import { useCsrf } from '../../../hooks/useCsrf';
import {
    SCENE_TEMPLATES,
    createScene,
    getScene,
    panelPositionLabels,
    panelSizeLabel,
    updateScene,
    type Scene,
    type SceneTemplateId,
    type ShellComponent,
} from '../../../data/ShellConfig';
import { track } from '../../../utils/mixpanel';
import { AnalyticsEvents } from '../../../analytics/events';
import SceneTemplatePicker from '../SceneTemplatePicker';
import PropEditor from '../PropEditor';
import ScenePreviewFrame, { type ScenePanelDraft } from './ScenePreviewFrame';
import StudioDrawer from './StudioDrawer';
import NewComponentDrawer from './NewComponentDrawer';

/** Sentinel option value in the Component dropdown that opens the New component drawer. */
const NEW_COMPONENT = '__new__';

type NewSceneDrawerProps = {
    opened: boolean;
    /** Dismiss / return to the Add-scene picker. */
    onClose: () => void;
    /** Available components for the panel dropdowns. */
    components: ShellComponent[];
    /** Called with the saved scene. */
    onSaved: (scene: Scene) => void;
    /** When set, edits an existing scene instead of creating a new one. */
    sceneId?: string;
    /** Overrides the drawer's stacking order — e.g. to open above a modal. */
    zIndex?: number;
};

const emptyPanel = (): ScenePanelDraft => ({ componentId: null, propOverrides: [] });

const PanelConfig: React.FC<{
    label: string;
    heightPercent: number;
    panel: ScenePanelDraft;
    components: ShellComponent[];
    onChange: (panel: ScenePanelDraft) => void;
    onNewComponent: () => void;
}> = ({ label, heightPercent, panel, components, onChange, onNewComponent }) => {
    const [showProps, setShowProps] = useState(false);
    const selected = components.find(component => component.id === panel.componentId);

    return (
        <Stack
            gap="xs"
            p="sm"
            style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8 }}
        >
            <Text size="sm" fw={600}>
                {label} • {panelSizeLabel(heightPercent)} px ({heightPercent}%)
            </Text>
            <Select
                label="Component"
                placeholder="Select…"
                data={[
                    { value: NEW_COMPONENT, label: '+ New component' },
                    ...components.map(component => ({ value: component.id, label: component.name })),
                ]}
                value={panel.componentId}
                onChange={value => {
                    if (value === NEW_COMPONENT) {
                        onNewComponent();
                        return;
                    }
                    onChange({
                        ...panel,
                        componentId: value,
                        // Clear overrides only when switching away from a prior component.
                        propOverrides:
                            value === panel.componentId || !panel.componentId
                                ? panel.propOverrides
                                : [],
                    });
                }}
                searchable
            />
            <UnstyledButton onClick={() => setShowProps(open => !open)}>
                <Group gap={4}>
                    <Text size="sm">Custom props</Text>
                    <IconChevronDown
                        size={14}
                        style={{
                            transform: showProps ? 'rotate(180deg)' : undefined,
                            transition: 'transform 120ms ease',
                        }}
                    />
                </Group>
            </UnstyledButton>
            <Collapse in={showProps}>
                <Stack gap="xs">
                    {selected && (
                        <Text size="xs" c="dimmed">
                            Overrides apply to this panel only. Defaults:{' '}
                            {(selected.props ?? []).map(p => `${p.key}=${p.value}`).join(', ') ||
                                'none'}
                        </Text>
                    )}
                    <PropEditor
                        props={panel.propOverrides}
                        onChange={propOverrides => onChange({ ...panel, propOverrides })}
                    />
                </Stack>
            </Collapse>
        </Stack>
    );
};

/**
 * Create or edit a scene: name, template, a component (and optional prop
 * overrides) per panel, with a live preview. Opens nested over the Add-scene
 * picker or the Library, so it uses the drawer's Back affordance rather than a
 * close X. Pass a `sceneId` to load an existing scene and save with an update.
 */
const NewSceneDrawer: React.FC<NewSceneDrawerProps> = ({
    opened,
    onClose,
    components,
    onSaved,
    sceneId,
    zIndex,
}) => {
    const { csrfToken } = useCsrf();
    const { mutate } = useSWRConfig();

    // Load the scene being edited; skipped entirely in create mode.
    const { data: editingScene } = useSWR(
        opened && sceneId ? `api/scenes/${sceneId}` : null,
        () => getScene(sceneId!)
    );
    const [name, setName] = useState('');
    const [templateId, setTemplateId] = useState<SceneTemplateId>('full');
    const [panels, setPanels] = useState<ScenePanelDraft[]>([emptyPanel()]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    // Panel index awaiting a component being created inline (null = drawer closed).
    const [newComponentForPanel, setNewComponentForPanel] = useState<number | null>(null);
    // Components created inline this session, so they're pickable/previewable
    // immediately without waiting on the parent's revalidation.
    const [extraComponents, setExtraComponents] = useState<ShellComponent[]>([]);

    const allComponents = useMemo(
        () => [...components, ...extraComponents],
        [components, extraComponents]
    );

    // Populate the form each time the drawer opens: from the loaded scene when
    // editing, or a blank scene when creating. Keyed on the scene id (not the
    // whole object) so background revalidation doesn't clobber in-progress edits.
    useEffect(() => {
        if (!opened) return;
        setError('');
        setExtraComponents([]);
        setNewComponentForPanel(null);
        if (sceneId) {
            if (!editingScene) return; // wait for the load
            setName(editingScene.name);
            setTemplateId(editingScene.templateId as SceneTemplateId);
            const ordered = [...(editingScene.panels ?? [])].sort((a, b) => a.index - b.index);
            setPanels(
                ordered.map(panel => ({
                    instanceId: panel.id,
                    componentId: panel.componentId,
                    propOverrides: panel.propOverrides ?? [],
                }))
            );
        } else {
            setName('');
            setTemplateId('full');
            setPanels([emptyPanel()]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, sceneId, editingScene?.id]);

    const handleNewComponentSaved = (component: ShellComponent) => {
        setExtraComponents(prev => [...prev, component]);
        setPanels(prev =>
            prev.map((panel, index) =>
                index === newComponentForPanel ? { ...panel, componentId: component.id } : panel
            )
        );
        void mutate('api/components');
    };

    // Grow/shrink the panel list to the chosen template, keeping existing picks.
    useEffect(() => {
        const template = SCENE_TEMPLATES.find(t => t.id === templateId)!;
        setPanels(prev => template.panels.map((_, index) => prev[index] ?? emptyPanel()));
    }, [templateId]);

    const template = SCENE_TEMPLATES.find(t => t.id === templateId)!;
    const positions = panelPositionLabels(template.panels.length);
    const valid = name.trim().length > 0 && panels.every(panel => panel.componentId);

    const handleSave = async () => {
        if (!valid) {
            setError('Name the scene and choose a component for every panel.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                name: name.trim(),
                templateId,
                panels: panels.map(panel => ({
                    instanceId: panel.instanceId,
                    componentId: panel.componentId!,
                    propOverrides: panel.propOverrides,
                })),
            };
            const saved = sceneId
                ? await updateScene(sceneId, payload, csrfToken)
                : await createScene(payload, csrfToken);
            track(sceneId ? AnalyticsEvents.SceneUpdated : AnalyticsEvents.SceneCreated, {
                source: 'studio_drawer',
                sceneId: saved.id,
                templateId,
                panelCount: payload.panels.length,
            });
            // Seed the detail cache so reopening the editor (same sceneId) reads
            // the freshly-saved panels instead of the stale pre-save snapshot.
            await mutate(`api/scenes/${saved.id}`, saved, { revalidate: false });
            onSaved(saved);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save the scene.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <StudioDrawer
            opened={opened}
            onClose={onClose}
            onBack={onClose}
            title={sceneId ? 'Edit scene' : 'New scene'}
            size="xl"
            zIndex={zIndex}
            footer={
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button onClick={handleSave} loading={saving} disabled={!valid}>
                        Save scene
                    </Button>
                </Group>
            }
        >
            <Stack gap="md">
                <TextInput
                    withAsterisk
                    label="Name"
                    placeholder="New scene"
                    value={name}
                    onChange={event => setName(event.currentTarget.value)}
                />

                <Grid gutter="lg">
                    <Grid.Col span={{ base: 12, md: 7 }}>
                        <Stack gap="md">
                            <SceneTemplatePicker templateId={templateId} onChange={setTemplateId} />
                            {panels.map((panel, index) => (
                                <PanelConfig
                                    key={index}
                                    label={positions[index]}
                                    heightPercent={template.panels[index]}
                                    panel={panel}
                                    components={allComponents}
                                    onChange={next =>
                                        setPanels(prev =>
                                            prev.map((p, i) => (i === index ? next : p))
                                        )
                                    }
                                    onNewComponent={() => setNewComponentForPanel(index)}
                                />
                            ))}
                        </Stack>
                    </Grid.Col>
                    <Grid.Col span={{ base: 12, md: 5 }}>
                        <ScenePreviewFrame
                            templateId={templateId}
                            panels={panels}
                            components={allComponents}
                            variant="live"
                            label="Preview"
                        />
                    </Grid.Col>
                </Grid>

                {error && (
                    <Alert color="red" icon={<IconExclamationCircle />}>
                        {error}
                    </Alert>
                )}
            </Stack>

            <NewComponentDrawer
                opened={newComponentForPanel !== null}
                onClose={() => setNewComponentForPanel(null)}
                onSaved={handleNewComponentSaved}
            />
        </StudioDrawer>
    );
};

export default NewSceneDrawer;
